/**
 * Per-recording channel layout (`app/services/channel_layout_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import type { SerializedLayout } from '@brainfocus/web/utils/channel_layout_serialization';
import { assertPojo } from '@brainfocus/web/api/response';

export async function getChannelLayout(
    recordingId: number,
): Promise<ChannelLayoutResponse> {
    const r = await client.request('GET', `/api/recordings/${recordingId}/channel-layout`);
    assertPojo<ChannelLayoutResponse>(r);
    return r;
}

export async function putChannelLayout(
    recordingId: number,
    groups: unknown[],
    montage?: string | null,
    selection?: number[] | null,
    appliedLayout?: { id: number; name: string } | null,
): Promise<void> {
    await client.request('PUT', `/api/recordings/${recordingId}/channel-layout`, {
        groups,
        montage,
        selection,
        applied_layout: appliedLayout,
    });
}

export async function deleteChannelLayout(recordingId: number): Promise<void> {
    await client.request('DELETE', `/api/recordings/${recordingId}/channel-layout`);
}

/**
 * Per-recording channel layout of the current user (#617) plus the recording's
 * label-based initial layout (#852) — the one the New Analysis dialog was
 * configured with, applied by the viewer while the user has no layout of their own.
 */
export interface ChannelLayoutResponse {
    groups: unknown[] | null;
    montage?: string | null;
    selection?: number[] | null;
    initial?: SerializedLayout | null;
    // The saved patient layout the arrangement came from (#1093).
    applied_layout?: { id: number; name: string } | null;
}
